import {
  discoveryApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import React, { useEffect, useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Typography,
  Box,
  TextField,
  MenuItem,
  Grid,
  Button,
  Snackbar,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import AddIcon from '@material-ui/icons/Add';
import { makeStyles } from '@material-ui/core/styles';
import { Asset, AssetCategory, AssetStatus, AssetFilter } from '../../types';
import { AssetRegisterService } from '../../services/assetRegisterService';
import { AddAssetDialog } from '../AddAssetDialog/AddAssetDialog';

const useStyles = makeStyles(theme => ({
  container: {
    padding: theme.spacing(3),
  },
  filters: {
    marginBottom: theme.spacing(3),
  },
  table: {
    marginTop: theme.spacing(2),
  },
  activeChip: {
    backgroundColor: theme.palette.success.main,
    color: 'white',
  },
  inactiveChip: {
    backgroundColor: theme.palette.grey[500],
    color: 'white',
  },
  maintenanceChip: {
    backgroundColor: theme.palette.warning.main,
    color: 'white',
  },
  retiredChip: {
    backgroundColor: theme.palette.error.main,
    color: 'white',
  },
  addButton: {
    marginBottom: theme.spacing(2),
  },
}));

export const AssetListView: React.FC = () => {
  const classes = useStyles();
  const [assets, setAssets] = useState<Asset[]>([]);
  const [filter, setFilter] = useState<AssetFilter>({});
  const [dialogOpen, setDialogOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [error, setError] = useState<string | null>(null);
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const service = React.useMemo(
    () => new AssetRegisterService(discoveryApi, fetchApi),
    [discoveryApi, fetchApi],
  );

  const fetchAssets = async () => {
    try {
      const result = await service.getAssets(filter);
      setAssets(result.assets);
      setError(null);
    } catch (err) {
      setError('Failed to load assets. Please try again.');
    }
  };

  useEffect(() => {
    fetchAssets();
  }, [filter]);

  const handleAddAsset = async (newAsset: Partial<Asset>) => {
    try {
      await service.addAsset(
        newAsset as Omit<Asset, 'id' | 'createdAt' | 'updatedAt'>,
      );
      await fetchAssets();
      setSnackbarMessage('Asset added successfully!');
      setSnackbarOpen(true);
    } catch (err) {
      setError('Failed to add asset. Please try again.');
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  const getStatusChip = (status: AssetStatus) => {
    const statusMap: Record<AssetStatus, { label: string; className: string }> =
      {
        [AssetStatus.UNKNOWN]: {
          label: 'Unverified',
          className: classes.inactiveChip,
        },
        [AssetStatus.ACTIVE]: {
          label: 'Active',
          className: classes.activeChip,
        },
        [AssetStatus.INACTIVE]: {
          label: 'Inactive',
          className: classes.inactiveChip,
        },
        [AssetStatus.IN_MAINTENANCE]: {
          label: 'Maintenance',
          className: classes.maintenanceChip,
        },
        [AssetStatus.RETIRED]: {
          label: 'Retired',
          className: classes.retiredChip,
        },
        [AssetStatus.DISPOSED]: {
          label: 'Disposed',
          className: classes.retiredChip,
        },
        [AssetStatus.LOST]: { label: 'Lost', className: classes.retiredChip },
        [AssetStatus.STOLEN]: {
          label: 'Stolen',
          className: classes.retiredChip,
        },
      };

    const { label, className } = statusMap[status];
    return <Chip label={label} size="small" className={className} />;
  };

  const formatCurrency = (amount: number, currency?: string) => {
    if (!currency) {
      return '—';
    }
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    }).format(amount);
  };

  return (
    <Box className={classes.container}>
      {error && (
        <Box mb={2}>
          <Alert severity="error">{error}</Alert>
        </Box>
      )}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Typography variant="h4">IT Asset Register</Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => setDialogOpen(true)}
          className={classes.addButton}
        >
          Add Asset
        </Button>
      </Box>

      <Paper className={classes.filters}>
        <Box p={2}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Search"
                variant="outlined"
                size="small"
                placeholder="Asset name, tag, serial..."
                onChange={e => setFilter({ ...filter, search: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                select
                label="Category"
                variant="outlined"
                size="small"
                value={filter.category || ''}
                onChange={e =>
                  setFilter({
                    ...filter,
                    category: e.target.value as AssetCategory,
                  })
                }
              >
                <MenuItem value="">All Categories</MenuItem>
                <MenuItem value={AssetCategory.HARDWARE}>Hardware</MenuItem>
                <MenuItem value={AssetCategory.SOFTWARE}>Software</MenuItem>
                <MenuItem value={AssetCategory.NETWORK}>Network</MenuItem>
                <MenuItem value={AssetCategory.INFRASTRUCTURE}>
                  Infrastructure
                </MenuItem>
                <MenuItem value={AssetCategory.MOBILE_DEVICE}>
                  Mobile Devices
                </MenuItem>
                <MenuItem value={AssetCategory.PERIPHERALS}>
                  Peripherals
                </MenuItem>
                <MenuItem value={AssetCategory.LICENSE}>Licenses</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                select
                label="Status"
                variant="outlined"
                size="small"
                value={filter.status || ''}
                onChange={e =>
                  setFilter({
                    ...filter,
                    status: e.target.value as AssetStatus,
                  })
                }
              >
                <MenuItem value="">All Statuses</MenuItem>
                <MenuItem value={AssetStatus.UNKNOWN}>Unverified</MenuItem>
                <MenuItem value={AssetStatus.ACTIVE}>Active</MenuItem>
                <MenuItem value={AssetStatus.INACTIVE}>Inactive</MenuItem>
                <MenuItem value={AssetStatus.IN_MAINTENANCE}>
                  In Maintenance
                </MenuItem>
                <MenuItem value={AssetStatus.RETIRED}>Retired</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Location"
                variant="outlined"
                size="small"
                placeholder="Filter by location"
                onChange={e =>
                  setFilter({ ...filter, location: e.target.value })
                }
              />
            </Grid>
          </Grid>
        </Box>
      </Paper>

      <TableContainer component={Paper} className={classes.table}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Asset Tag</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Location</TableCell>
              <TableCell>Assigned To</TableCell>
              <TableCell>Purchase Date</TableCell>
              <TableCell align="right">Value</TableCell>
              <TableCell>Warranty</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {assets.map(asset => (
              <TableRow key={asset.id} hover>
                <TableCell>
                  <Typography variant="body2" style={{ fontWeight: 600 }}>
                    {asset.assetTag}
                  </Typography>
                </TableCell>
                <TableCell>{asset.name}</TableCell>
                <TableCell>
                  <Chip
                    label={asset.category}
                    size="small"
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>{getStatusChip(asset.status)}</TableCell>
                <TableCell>{asset.location}</TableCell>
                <TableCell>{asset.assignedTo || '-'}</TableCell>
                <TableCell>
                  {asset.purchaseDate
                    ? new Date(asset.purchaseDate).toLocaleDateString()
                    : '—'}
                </TableCell>
                <TableCell align="right">
                  {formatCurrency(
                    asset.depreciation?.currentValue || asset.purchaseCost,
                    asset.currency,
                  )}
                </TableCell>
                <TableCell>
                  {asset.warrantyExpiry
                    ? new Date(asset.warrantyExpiry).toLocaleDateString()
                    : '-'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {assets.length === 0 && (
        <Box mt={4} textAlign="center">
          <Typography variant="body1" color="textSecondary">
            No assets found matching your criteria
          </Typography>
        </Box>
      )}

      <AddAssetDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSave={handleAddAsset}
      />

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity="success">
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};
